import { SYNC_KEY_PROYECTOS_PROYECTOS } from "../../../constantes";
import {
  ProyectoItemDetalleItem,
  ProyectoPlataformaItem,
  ProyectosPlataformaService,
  SolicitudProyectoItem,
} from "../../../shared/generated/grpc/plataforma_proyectos_pb";
import {
  EstadoSolicitud,
  ProyectoDetalleResponse,
  ProyectoEstado,
  ProyectoResponse,
  PresupuestoResponse,
  Semaforo,
  SolicitudResponse,
} from "../../../shared/response/proyectos/ProyectoResponse";
import {
  ProyectoEstadoPayload,
  ProyectoItemsPayload,
  ProyectoItemPatchPayload,
} from "../../../shared/services/api/payload/proyectos/ProyectoPayload";
import { ServiceGetListBase } from "../../../shared/services/base/ServiceGetListBase";
import { serviceSync } from "../../../shared/services/base/ServiceSync";
import { grpcCall, grpcClient } from "../../../shared/services/grpc/grpcCall";
import { toGastoResponse } from "./GastoService";

/** Proyectos (`ProyectosPlataformaService`, gRPC): listado en memoria, portal del técnico, detalle y presupuesto. */
export class ProyectoService extends ServiceGetListBase<ProyectoResponse> {
  static readonly instance = new ProyectoService();
  private constructor() { super(); }

  /** Clave única del listado: deduplica `sync()` (dos pedidos simultáneos, un solo request). */
  protected readonly syncKey = SYNC_KEY_PROYECTOS_PROYECTOS;

  // Listado propio: el portal del técnico ve otro conjunto de proyectos que el admin.
  private _misProyectos: ProyectoResponse[] = [];

  /** gRPC-Web (`ListProyectos`): no eliminados. */
  protected async fetchItems(): Promise<ProyectoResponse[]> {
    const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).listProyectos({}));
    return r.items.map(toProyectoResponse);
  }

  /** Proyectos del técnico de la sesión (`ListMisProyectosTecnico`); sin conexión devuelve lo último cargado. */
  async getMisProyectosTecnico(): Promise<ProyectoResponse[]> {
    await serviceSync
      .run(`${this.syncKey}/mis-proyectos-tecnico`, async () => {
        const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).listMisProyectosTecnico({}));
        this._misProyectos = r.items.map(toProyectoResponse);
      })
      .catch(() => { /* offline → devuelve lo último cargado */ });
    return this._misProyectos;
  }

  /** Proyecto + KPIs + ítems con sus gastos (`GetDetalle`). */
  async getDetalle(id: string): Promise<ProyectoDetalleResponse> {
    const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).getDetalle({ id }));
    if (!r.proyecto || !r.kpis) throw new Error("Respuesta sin proyecto");
    return {
      proyecto: toProyectoResponse(r.proyecto),
      kpis: {
        presupuestoTotal: Number(r.kpis.presupuestoTotal),
        ejecutadoTotal: Number(r.kpis.ejecutadoTotal),
        disponible: Number(r.kpis.disponible),
        desviacionGlobal: r.kpis.desviacionGlobal,
        semaforo: r.kpis.semaforo as Semaforo,
        itemsEnAlerta: r.kpis.itemsEnAlerta,
      },
      items: r.items.map(toItemDetalle),
    };
  }

  /**
   * Presupuesto hasta cosecha (`GetPresupuesto`): CAPEX en el año 1 y OPEX con IPC compuesto año a año,
   * con los parámetros congelados al crear el proyecto. La serie anual suma exactamente los agregados.
   */
  async getPresupuesto(id: string): Promise<PresupuestoResponse> {
    const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).getPresupuesto({ id }));
    return {
      proyectoId: id,
      ha: r.ha,
      mesesACosecha: r.mesesACosecha,
      ipc: r.ipc,
      factorJornal: Number(r.factorJornal),
      capex: Number(r.capex),
      opexProyectado: Number(r.opexProyectado),
      total: Number(r.total),
      detalleAnual: r.detalleAnual.map((a) => ({
        anio: a.anio,
        fraccion: a.fraccion,
        opex: Number(a.opex),
        capex: Number(a.capex),
        total: Number(a.total),
      })),
      itemsSinPrecio: [...r.itemsSinPrecio],
      presupuestoCompleto: r.presupuestoCompleto,
    };
  }

  /**
   * Edita UN ítem del presupuesto (`ActualizarItem`): manda solo el campo que cambió, así dos personas que editan
   * ítems distintos del mismo proyecto no se pisan. Lo usa la edición en línea de la tabla del detalle, del admin y
   * del técnico (el Server le deja al técnico cambiar solo el avance); `saveItems` (reemplazo completo) queda para
   * el wizard. `itemId` = id del CatalogoItem. No devuelve nada: quien llama recarga el detalle.
   */
  async patchItem(proyectoId: string, itemId: string, payload: ProyectoItemPatchPayload): Promise<void> {
    await grpcCall(() => grpcClient(ProyectosPlataformaService).actualizarItem({
      proyectoId,
      itemId,
      cantidad: payload.cantidad,
      activo: payload.activo,
      avanceEstado: payload.avanceEstado,
    }));
  }

  /**
   * Reemplazo completo de ítems del proyecto (`GuardarItems`). Un ítem nuevo sin precio toma el vigente del
   * catálogo; uno existente conserva su precio congelado. `avanceEstado` null = no tocar el que ya marcó el técnico.
   */
  async saveItems(id: string, payload: ProyectoItemsPayload): Promise<ProyectoResponse> {
    const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).guardarItems({
      proyectoId: id,
      items: payload.items.map((i) => ({
        itemId: i.itemId,
        cantidad: i.cantidad,
        precioUnitario: i.precioUnitario !== null ? BigInt(Math.round(i.precioUnitario)) : undefined,
        activo: i.activo,
        avanceEstado: i.avanceEstado ?? undefined,
      })),
    }));
    const item = toProyectoResponse(r);
    this.upsert(item);
    return item;
  }

  /**
   * Cambia el estado (`CambiarEstado`), un paso a la vez: el servidor rechaza las transiciones no permitidas
   * y deja la entrada en la bitácora.
   */
  async cambiarEstado(id: string, payload: ProyectoEstadoPayload): Promise<ProyectoResponse> {
    const r = await grpcCall(() => grpcClient(ProyectosPlataformaService).cambiarEstado({ id, estado: payload.estado }));
    const item = toProyectoResponse(r);
    this.upsert(item);
    return item;
  }
}

/** Mensaje gRPC del proyecto → `ProyectoResponse`. Lo reusa el guardado. */
export function toProyectoResponse(p: ProyectoPlataformaItem): ProyectoResponse {
  return {
    id: p.id,
    isEliminado: false,
    nombre: p.nombre,
    estado: p.estado as ProyectoEstado,
    clienteId: p.clienteId,
    clienteNombre: p.clienteNombre,
    cultivoId: p.cultivoId,
    cultivoNombre: p.cultivoNombre,
    cultivoMesesACosecha: p.cultivoMesesACosecha,
    snapshotAño: p.snapshotAnio,
    snapshotIpc: p.snapshotIpc,
    snapshotFactorJornal: Number(p.snapshotFactorJornal),
    tecnicoId: p.tecnicoId,
    tecnicoNombre: p.tecnicoNombre,
    fincaId: p.fincaId ?? null,
    variedad: p.variedad ?? null,
    municipio: p.municipio ?? null,
    ha: p.ha,
    fechaInicio: p.fechaInicio !== undefined ? new Date(Number(p.fechaInicio)).toISOString() : null,
    nota: p.nota ?? null,
    macroProyectoId: p.macroProyectoId ?? null,
    macroProyectoNombre: p.macroProyectoNombre ?? null,
    nucleoId: p.nucleoId ?? null,
    nucleoNombre: p.nucleoNombre ?? null,
    duracionMeses: p.duracionMeses ?? null,
    entidadFinancieraReferencia: p.entidadFinancieraReferencia ?? null,
    limiteFinancieroReferencial: p.limiteFinancieroReferencial !== undefined ? Number(p.limiteFinancieroReferencial) : null,
    totalItems: p.totalItems,
    itemsActivos: p.itemsActivos,
    itemsSinPrecio: p.itemsSinPrecio,
    capex: Number(p.capex),
    opexAnual: Number(p.opexAnual),
    ejecutado: Number(p.ejecutado),
    createdAt: Number(p.createdAt),
  };
}

/** Ítem del detalle; sin precio o sin presupuesto, presupuestado/desviación/avance quedan en 0. */
function toItemDetalle(i: ProyectoItemDetalleItem): ProyectoDetalleResponse["items"][number] {
  return {
    id: i.id,
    itemId: i.itemId,
    sec: i.sec,
    sub: i.sub,
    tipo: i.tipo,
    nombre: i.nombre,
    categoria: i.categoria,
    unidad: i.unidad,
    cantUnidad: i.cantUnidad,
    tipoP: i.tipoP,
    cantidad: i.cantidad,
    freq: i.freq,
    faseId: i.faseId ?? null,
    faseCodigo: i.faseCodigo ?? null,
    precioUnitario: i.precioUnitario !== undefined ? Number(i.precioUnitario) : null,
    presupuestado: i.presupuestado !== undefined ? Number(i.presupuestado) : 0,
    ejecutado: Number(i.ejecutado),
    desviacion: i.desviacion ?? 0,
    avance: i.avance ?? 0,
    avanceEstado: i.avanceEstado,
    activo: i.activo,
    nitro: i.nitro,
    gastos: i.gastos.map(toGastoResponse),
    solicitudes: i.solicitudes.map(toSolicitudResponse),
  };
}

/** Mensaje gRPC de la solicitud → `SolicitudResponse`. Lo reusa el alta (`SolicitudSaveService`). */
export function toSolicitudResponse(s: SolicitudProyectoItem): SolicitudResponse {
  return {
    id: s.id,
    isEliminado: false,
    fecha: Number(s.fecha),
    cantidad: s.cantidad,
    entregado: s.entregado,
    estado: s.estado as EstadoSolicitud,
    observaciones: s.observaciones ?? null,
    solicitadoPor: s.solicitadoPor ?? null,
  };
}
